import React, { useState } from 'react';
import { Box, InputBase } from '@mui/material';
import { Search } from 'lucide-react';
import { EstadoBolsa } from '../state/bolsaMachine';
import { centroApi } from '../api/centroApi';
import { CentroApiError, BolsaCentro, ReprecioResumen, VersionCentro } from '../types';
import { TOKENS } from '../state/colors';
import { fs } from '../state/typography';
import { useIconScale } from '../state/useIconScale';
import { BagStrip } from './BagStrip';
import { ChannelChoiceModal } from './ChannelChoiceModal';
import { KanbanBoard } from './KanbanBoard';
import { ManualSendModal } from './ManualSendModal';
import { SectionCard } from './SectionCard';
import { SendQuoteModal } from './SendQuoteModal';
import { ReprecioResumenModal } from './ReprecioResumenModal';
import { useToast } from './Toast';
import { clienteService } from '../../../../clientes/services/clienteService';

interface PipelineTabProps {
  bolsasAbiertas: BolsaCentro[];
  cotizacionesPipeline: BolsaCentro[];
  busquedaPipeline: string;
  onBuscarPipeline: (valor: string) => void;
  onRefrescar: () => void;
  onAbrirDrawer: (id: string) => void;
  onVerDetalle: (id: string) => void;
}

export const PipelineTab: React.FC<PipelineTabProps> = ({
  bolsasAbiertas,
  cotizacionesPipeline,
  busquedaPipeline,
  onBuscarPipeline,
  onRefrescar,
  onAbrirDrawer,
  onVerDetalle,
}) => {
  const { showToast } = useToast();
  const searchIconSize = useIconScale(15);
  const [envioPendiente, setEnvioPendiente] = useState<{
    bolsa: BolsaCentro;
    siguienteVersion: number;
    versionVigente: VersionCentro | null;
  } | null>(null);
  // SPEC-206 (CA-008) — resumen del re-precio al arrastrar ENVIADA o VENCIDA a Borrador.
  const [resumenReprecio, setResumenReprecio] = useState<{ bolsaId: string; reprecio: ReprecioResumen; subtitulo: string } | null>(null);
  const [canalPendiente, setCanalPendiente] = useState<BolsaCentro | null>(null);
  const [manualPendiente, setManualPendiente] = useState<BolsaCentro | null>(null);

  const handleConvertida = (bolsaId: string) => {
    onRefrescar();
    onAbrirDrawer(bolsaId);
  };

  // SPEC-180 UC-001/RN-001 — siempre pregunta el canal antes de transicionar a ENVIADA
  const handleSoltarEnviada = (bolsaId: string) => {
    const bolsa = cotizacionesPipeline.find((b) => b.id === bolsaId);
    if (!bolsa) return;
    setCanalPendiente(bolsa);
  };

  // SPEC-180 UC-002/CA-002 — flujo de correo, idéntico al comportamiento previo
  const handleElegirCorreo = async (bolsa: BolsaCentro) => {
    setCanalPendiente(null);
    try {
      const detalle = await centroApi.obtenerDetalle(bolsa.id);

      // RN-001 — bloquea la transición antes de abrir el modal si el cliente no tiene
      // ningún contacto activo con correo (defensa en profundidad además de EnviarCotizacionUseCase)
      if (!detalle.bolsa.cliente_id) {
        showToast('Asocia un cliente con un contacto de correo antes de enviar', 'error');
        return;
      }
      const cliente = await clienteService.obtener(detalle.bolsa.cliente_id);
      const hayContactoElegible = cliente.contactos.some((c) => c.activo && !!c.correoElectronico?.trim());
      if (!hayContactoElegible) {
        showToast('Asocia un cliente con un contacto de correo antes de enviar', 'error');
        return;
      }

      const vigente = detalle.versiones.find((v) => v.es_vigente) ?? null;
      setEnvioPendiente({
        bolsa: detalle.bolsa,
        siguienteVersion: detalle.siguiente_version ?? (vigente?.version ?? 0) + 1,
        versionVigente: vigente,
      });
    } catch {
      showToast('No se pudo abrir el envío', 'error');
    }
  };

  // SPEC-180 UC-003/UC-004/CA-006 — vía manual: valida cliente localmente (RN-004), sin API extra
  const handleElegirManual = (bolsa: BolsaCentro) => {
    setCanalPendiente(null);
    if (!bolsa.cliente_id) {
      showToast('Asocia un cliente a la cotización antes de marcarla como enviada', 'error');
      return;
    }
    setManualPendiente(bolsa);
  };

  // SPEC-180 UC-003/UC-005/UC-006 — confirma vía el endpoint genérico ya existente
  const handleConfirmarManual = async (nota: string) => {
    if (!manualPendiente) return;
    const bolsa = manualPendiente;
    setManualPendiente(null);
    try {
      const { bolsa: actualizada } = await centroApi.transicionar(bolsa.id, EstadoBolsa.ENVIADA, nota.trim() || undefined);
      showToast(`${actualizada.codigo_cotizacion ?? actualizada.id} marcada como enviada manualmente`);
      onRefrescar();
    } catch (error) {
      const message = error instanceof CentroApiError ? error.message : 'No se pudo marcar como enviada';
      showToast(message, 'error');
    }
  };

  const handleTransicionar = async (bolsaId: string, to: EstadoBolsa) => {
    try {
      const { bolsa, reprecio } = await centroApi.transicionar(bolsaId, to);
      showToast(`${bolsa.codigo_cotizacion ?? bolsa.id} → ${to}`);
      if (reprecio) {
        setResumenReprecio({ bolsaId, reprecio, subtitulo: `${bolsa.codigo_cotizacion ?? bolsa.id} quedó en borrador` });
      }
      onRefrescar();
    } catch (error) {
      const message = error instanceof CentroApiError ? error.message : 'No se pudo transicionar';
      showToast(message, 'error');
    }
  };

  return (
    <Box data-testid="tab-panel-pipeline">
      <SectionCard title="Bolsas abiertas · el carrito, antes de la cotización">
        <BagStrip bolsasAbiertas={bolsasAbiertas} onConvertida={handleConvertida} />
      </SectionCard>
      <SectionCard title="Ciclo de la cotización">
        {/* SPEC-113 (RN-002, RN-003) — buscador por cliente, independiente del de Cotizaciones */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            borderBottom: `1px solid ${TOKENS.hair}`,
            paddingBottom: '6px',
            minWidth: 344,
            marginBottom: '18px',
          }}
        >
          <Search size={searchIconSize} color={TOKENS.faint} />
          <InputBase
            data-testid="pipeline-search"
            placeholder="Buscar por cliente…"
            value={busquedaPipeline}
            onChange={(e) => onBuscarPipeline(e.target.value)}
            sx={{ fontFamily: 'Poppins, sans-serif', fontSize: fs(15), flex: 1 }}
          />
        </Box>
        <KanbanBoard
          cotizaciones={cotizacionesPipeline}
          onAbrirDrawer={onAbrirDrawer}
          onSoltarEnviada={handleSoltarEnviada}
          onTransicionar={handleTransicionar}
          onVerDetalle={onVerDetalle}
        />
      </SectionCard>

      {canalPendiente && (
        <ChannelChoiceModal
          bolsa={canalPendiente}
          onCerrar={() => setCanalPendiente(null)}
          onElegirCorreo={() => handleElegirCorreo(canalPendiente)}
          onElegirManual={() => handleElegirManual(canalPendiente)}
        />
      )}
      {manualPendiente && (
        <ManualSendModal bolsa={manualPendiente} onCerrar={() => setManualPendiente(null)} onConfirmar={handleConfirmarManual} />
      )}
      {envioPendiente && (
        <SendQuoteModal
          bolsa={envioPendiente.bolsa}
          siguienteVersion={envioPendiente.siguienteVersion}
          versionVigente={envioPendiente.versionVigente}
          onCerrar={() => setEnvioPendiente(null)}
          onEnviada={onRefrescar}
        />
      )}
      {resumenReprecio && (
        <ReprecioResumenModal
          reprecio={resumenReprecio.reprecio}
          subtitulo={resumenReprecio.subtitulo}
          onCerrar={() => setResumenReprecio(null)}
          onRevisar={() => {
            setResumenReprecio(null);
            onVerDetalle(resumenReprecio.bolsaId);
          }}
        />
      )}
    </Box>
  );
};
